import {
  ApiError,
  FormsService,
  FormSummary,
  ManageCrmService,
  OrganizationDataKeyResponse,
  OrganizationResponseForAdmins,
  OrganizationSubjectPublicResponse,
  OrgSubjectsService,
} from "client/openapi";
import CRMdata from "./data/CRMdata";
// import { CRMsearch } from "./search/CRMsearch";
import { useEffect, useState } from "react";
import Notifications from "util/notifications";
import { CRMsearch } from "./search/search";
import VisualizationView from "./visualize/VisualizationView";

export default function CRM({
  organization,
}: {
  organization: OrganizationResponseForAdmins;
}) {
  const [activeView, setActiveView] = useState<"data" | "visualize" | "search">(
    "data"
  );
  const [error, setError] = useState<ApiError>();
  const [studentKeys, setStudentKeys] = useState<OrganizationDataKeyResponse[]>(
    []
  );
  const [parentKeys, setParentKeys] = useState<OrganizationDataKeyResponse[]>(
    []
  );
  const [tutorKeys, setTutorKeys] = useState<OrganizationDataKeyResponse[]>([]);
  const [subjects, setSubjects] = useState<OrganizationSubjectPublicResponse[]>(
    []
  );
  const [forms, setForms] = useState<FormSummary[]>([]);

  const [loaded, setLoaded] = useState(false);

  // The field lists (and the subjects and forms some built-in fields pick
  // from) for Data, Visualize and Search, loaded once. One failed list never
  // holds the others back.
  useEffect(() => {
    const orgId = organization.id;
    setLoaded(false);
    Promise.allSettled([
      ManageCrmService.getStudentDataKeys({ orgId }).then(setStudentKeys),
      ManageCrmService.getParentDataKeys({ orgId }).then(setParentKeys),
      ManageCrmService.getTutorDataKeys({ orgId }).then(setTutorKeys),
      OrgSubjectsService.getSubjectsByOrg({ orgId, active: true }).then(
        (orgSubjects) =>
          setSubjects(orgSubjects as OrganizationSubjectPublicResponse[])
      ),
      FormsService.getForms({ orgId }).then(setForms),
    ]).then((results) => {
      const failed = results.find((r) => r.status === "rejected");
      if (failed) setError((failed as PromiseRejectedResult).reason);
      setLoaded(true);
    });
  }, [organization.id]);

  useEffect(() => {
    if (error) {
      Notifications.error(
        error.body?.detail ?? error.body?.error ?? "Could not load CRM fields"
      );
    }
  }, [error]);

  return (
    <div className="flex flex-col items-center w-full">
      <div className="inline-flex rounded-full border border-[#98acfc] p-1 mb-6 mt-4">
        <button
          className={`px-4 py-2 rounded-full text-sm transition-colors duration-200 ${
            activeView === "data"
              ? "bg-[#98acfc] text-white"
              : "text-[#98acfc] hover:bg-[#98acfc]/10"
          }`}
          onClick={() => setActiveView("data")}
        >
          Data
        </button>
        <button
          className={`px-4 py-2 rounded-full text-sm transition-colors duration-200 ${
            activeView === "visualize"
              ? "bg-[#98acfc] text-white"
              : "text-[#98acfc] hover:bg-[#98acfc]/10"
          }`}
          onClick={() => setActiveView("visualize")}
        >
          Visualize
        </button>
        <button
          className={`px-4 py-2 rounded-full text-sm transition-colors duration-200 ${
            activeView === "search"
              ? "bg-[#98acfc] text-white"
              : "text-[#98acfc] hover:bg-[#98acfc]/10"
          }`}
          onClick={() => setActiveView("search")}
        >
          Search
        </button>
      </div>

      {!loaded ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-900"></div>
        </div>
      ) : activeView === "data" ? (
        <CRMdata
          organization={organization}
          studentKeys={studentKeys}
          setStudentKeys={setStudentKeys}
          parentKeys={parentKeys}
          setParentKeys={setParentKeys}
          tutorKeys={tutorKeys}
          setTutorKeys={setTutorKeys}
        />
      ) : activeView === "visualize" ? (
        <VisualizationView
          orgId={organization.id}
          organization={organization}
          organizationName={organization.name}
          studentKeys={studentKeys}
          parentKeys={parentKeys}
          tutorKeys={tutorKeys}
          subjects={subjects}
          forms={forms}
        />
      ) : (
        <CRMsearch
          orgId={organization.id}
          studentKeys={studentKeys}
          parentKeys={parentKeys}
          tutorKeys={tutorKeys}
          subjects={subjects}
          forms={forms}
        />
      )}
    </div>
  );
}
